import { useMemo } from "react";
import {
  useFormAnswers,
  useFormTemplate,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import * as Question from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";

export type VerificationModalFile = {
  id: string;
  /**
   * True if this document appears from any file-upload question whose verification has Identity AI
   * (`is_identity_ai_assisted`).
   */
  identityAiEligible: boolean;
};

export type UseVerificationModalFilesArgs = {
  rankedSchoolIds: uuid[];
  previousFormSchoolIds: uuid[];
};

/**
 * Form-wide files for the verification document viewer (applicable file uploads, deduped).
 * Must be used within FormProvider (template, answers map, and visibility context).
 *
 * NOTE: Not updated/tested for Avela AI (Docsense) flow; only tested for Identity AI flow.
 */
export function useVerificationModalFiles({
  rankedSchoolIds,
  previousFormSchoolIds,
}: UseVerificationModalFilesArgs): VerificationModalFile[] {
  const formTemplate = useFormTemplate();
  const formAnswersMap = useFormAnswers();
  const visibilityContext = useVisibilityContext();

  return useMemo(() => {
    const fullFormAnswers = Object.fromEntries(formAnswersMap);
    const allComplete = Question.getFormCompleteQuestions(formTemplate);
    const applicable = Question.getCompleteApplicableQuestions(
      allComplete,
      fullFormAnswers,
      { rankedSchoolIds, previousFormSchoolIds },
      visibilityContext
    );

    const files: VerificationModalFile[] = [];

    for (const q of applicable) {
      if (q.type !== AF.FileUploadType) {
        continue;
      }
      const answer = fullFormAnswers[q.id];
      if (!Array.isArray(answer)) {
        continue;
      }
      const questionEligible =
        q.formVerification?.is_identity_ai_assisted ?? false;
      for (const documentId of answer) {
        const existing = files.find((r) => r.id === documentId);
        if (existing) {
          existing.identityAiEligible =
            existing.identityAiEligible || questionEligible;
        } else {
          files.push({
            id: documentId,
            identityAiEligible: questionEligible,
          });
        }
      }
    }

    return files;
  }, [
    formAnswersMap,
    formTemplate,
    rankedSchoolIds,
    previousFormSchoolIds,
    visibilityContext,
  ]);
}
